import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Edit2, Save, X, Trash2, FolderPlus, ChevronRight, ChevronLeft, Search, Mic, Square, PlusCircle, ArrowLeft, Check, XCircle, BookOpen, Info, ImagePlus, Eye, EyeOff, Loader2, UserPlus, Send, Users, MessageCircle, Pause, Play, MoreHorizontal, FileText, QrCode, FolderOpen, Copy, Link2, Palette, Shield } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { useRecordingContext } from '../contexts/RecordingContext';
import { LinkifyText } from './LinkifyText';
import ShareModal from './ShareModal';
import SwipeToDelete from './SwipeToDelete';
import { ProgressBar } from './ui/ProgressBar';
import StoryComments from './StoryComments';
import ImageViewerModal from './ImageViewerModal';
import { useMobileDisplayCheck } from '../hooks/useMobileDisplayCheck';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pageVariants = {
  initial: { opacity: 0, x: 30 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 }
};

const pageTransition = { duration: 0.25, ease: 'easeOut' };

const FOLDER_COLORS = [
  { name: 'Red', hex: '#E74C3C' },
  { name: 'Orange', hex: '#F39C12' },
  { name: 'Yellow', hex: '#F1C40F' },
  { name: 'Green', hex: '#2ECC71' },
  { name: 'Teal', hex: '#1ABC9C' },
  { name: 'Blue', hex: '#3498DB' },
  { name: 'Indigo', hex: '#5B5EA6' },
  { name: 'Purple', hex: '#9B59B6' },
  { name: 'Pink', hex: '#E91E8F' },
  { name: 'Grey', hex: '#7F8C8D' },
];

// Helper: hex to rgba
function hexToRgba(hex, alpha) {
  if (!hex) return 'transparent';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function TopicPage({ user }) {
  const { topicId, folderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Mobile display check - auto-reload if page doesn't render properly
  useMobileDisplayCheck('[data-testid="topic-page-content"]');
  
  // Detect shared mode from URL OR if folder has collaborators
  const isSharedMode = location.pathname.includes('/stories/shared/');
  const effectiveId = isSharedMode ? folderId : topicId;
  const [isSharedWithMe, setIsSharedWithMe] = useState(false);
  const [hasCollaborators, setHasCollaborators] = useState(false); // Track if folder has any collab
  const [collaboratorsList, setCollaboratorsList] = useState([]); // List of collaborator details for avatars
  const [isFolderOwner, setIsFolderOwner] = useState(false); // Whether current user owns this folder
  
  // Show comments if in shared mode OR if folder has collaborators OR Shared with Me
  const showCommentsFeature = isSharedMode || hasCollaborators || isSharedWithMe;
  
  // Optimistic loading: use nav state for instant folder header, localStorage for cached stories
  const navFolder = location.state?.folder || null;
  const [isLoadingFolder, setIsLoadingFolder] = useState(() => {
    // If we have nav state or cached stories, don't show loading
    if (navFolder) return false;
    try { return !localStorage.getItem(`noyo_folder_stories_${isSharedMode ? folderId : topicId}`); } catch { return true; }
  });
  const [experiences, setExperiences] = useState(() => {
    try {
      const cached = localStorage.getItem(`noyo_folder_stories_${isSharedMode ? folderId : topicId}`);
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [folder, setFolder] = useState(navFolder || null);
  const [folders, setFolders] = useState([]);
  const [editingExpId, setEditingExpId] = useState(null);
  const [editExpData, setEditExpData] = useState({ summary: '', topic_folder: '' });
  const [deleteExpConfirm, setDeleteExpConfirm] = useState(null);
  const [showAddStory, setShowAddStory] = useState(false);
  const [newStorySummary, setNewStorySummary] = useState('');
  const [isSearchingStories, setIsSearchingStories] = useState(false);
  const [showEditDescription, setShowEditDescription] = useState(false);
  const [editDescription, setEditDescription] = useState('');
  const [showEditTitle, setShowEditTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [addToFolderOpen, setAddToFolderOpen] = useState(null);
  const [inlineFolderName, setInlineFolderName] = useState('');
  const [isCreatingInlineFolder, setIsCreatingInlineFolder] = useState(false);
  const [pendingSelections, setPendingSelections] = useState(new Set());
  const [initialSelections, setInitialSelections] = useState(new Set());
  const [pendingNewFolders, setPendingNewFolders] = useState([]);
  const [isSavingFolders, setIsSavingFolders] = useState(false);
  const [addingToFolder, setAddingToFolder] = useState(null);
  const [quickStory, setQuickStory] = useState('');
  const [isAddingQuickStory, setIsAddingQuickStory] = useState(false);
  const [topicSaveProgress, setTopicSaveProgress] = useState(0);
  const [isTopicSaving, setIsTopicSaving] = useState(false);
  const [isRecordingQuick, setIsRecordingQuick] = useState(false);
  const [isPausedQuick, setIsPausedQuick] = useState(false);
  const [showFolderTour, setShowFolderTour] = useState(false);
  const [folderTourStep, setFolderTourStep] = useState(0);
  const [tourTargetRect, setTourTargetRect] = useState(null);
  
  // Global recording context - disables swipe navigation while recording
  const { setIsRecording: setGlobalRecording } = useRecordingContext();
  const [isTranscribingQuick, setIsTranscribingQuick] = useState(false);
  const [transcriptionProgress, setTranscriptionProgress] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [quickAudioBlob, setQuickAudioBlob] = useState(null);
  const [autoSortToast, setAutoSortToast] = useState(null);
  const [showJournalPrompt, setShowJournalPrompt] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [storyCount, setStoryCount] = useState(0);
  const [expandedStories, setExpandedStories] = useState({});
  const [deleteFolderConfirm, setDeleteFolderConfirm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showFolderMenu, setShowFolderMenu] = useState(false);
  const [activeSection, setActiveSection] = useState(null); // null | 'share' | 'qr' | 'find' | 'edit' | 'color'
  const [isEditingFolder, setIsEditingFolder] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  // Inline share state
  const [shareLink, setShareLink] = useState(null);
  const [shareLinkLoading, setShareLinkLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [selectedInviteEmail, setSelectedInviteEmail] = useState(null);
  const [inviteSearchResults, setInviteSearchResults] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [recentCollabs, setRecentCollabs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('noyo_recent_collabs') || '[]'); } catch { return []; }
  });
  // Inline find state
  const [pendingStories, setPendingStories] = useState([]);
  const [findLoading, setFindLoading] = useState(false);
  const [findSearched, setFindSearched] = useState(false);
  // Story card overflow menu
  const [storyMenuOpen, setStoryMenuOpen] = useState(null); // exp.id or null
  const storyMenuRef = useRef(null);
  const [shareStoryId, setShareStoryId] = useState(null);
  const [quickImage, setQuickImage] = useState(null);
  const [quickImages, setQuickImages] = useState([]); // [{file, preview}]
  const [summarizingId, setSummarizingId] = useState(null);
  const [summarizeProgress, setSummarizeProgress] = useState(0);
  const [showAiSummary, setShowAiSummary] = useState({});
  const [generatingSummary, setGeneratingSummary] = useState({});
  const [openCommentsId, setOpenCommentsId] = useState(null); // Which story has comments open
  const [uploadingImageForStory, setUploadingImageForStory] = useState(null); // Track image upload per story
  const storyImageInputRef = useRef(null);
  const pendingStoryImageId = useRef(null);
  const [commentCounts, setCommentCounts] = useState({});
  const [perspectives, setPerspectives] = useState({}); // { storyId: [perspective, ...] }
  const [perspectiveInput, setPerspectiveInput] = useState({}); // { storyId: text }
  const [submittingPerspective, setSubmittingPerspective] = useState(null);
  const [editingPerspectiveId, setEditingPerspectiveId] = useState(null);
  const [editPerspectiveContent, setEditPerspectiveContent] = useState('');
  const [viewerImages, setViewerImages] = useState(null);
  const [viewerInitialIndex, setViewerInitialIndex] = useState(0);
  const STORY_MAX_LENGTH = 730;
  const quickMediaRecorderRef = useRef(null);
  const quickAudioChunksRef = useRef([]);
  const quickStreamRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const summarizeIntervalRef = useRef(null);

  const [highlightedStoryId, setHighlightedStoryId] = useState(null);
  const pendingStoryRef = useRef(null);
  const pendingCommentsRef = useRef(false);

  useEffect(() => {
    fetchData();
  }, [effectiveId, isSharedMode]);

  // Close story menu on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (storyMenuRef.current && !storyMenuRef.current.contains(e.target)) setStoryMenuOpen(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Fetch share link when share section opens
  useEffect(() => {
    if (activeSection === 'share' && !shareLink) fetchShareLink();
  }, [activeSection]);

  // Auto-refresh for collaborative folders (poll every 15 seconds)
  useEffect(() => {
    if (!showCommentsFeature && !isSharedMode) return;
    
    const pollInterval = setInterval(() => {
      fetchData();
    }, 15000); // Refresh every 15 seconds for collab folders
    
    return () => clearInterval(pollInterval);
  }, [effectiveId, isSharedMode, showCommentsFeature]);

  const [wiggleFolder, setWiggleFolder] = useState(false);

  // Handle story highlighting from search or notification
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const storyId = params.get('story');
    const openComments = params.get('comments') === 'true';
    
    // Store pending navigation params
    if (storyId) {
      pendingStoryRef.current = storyId;
      pendingCommentsRef.current = openComments;
    }
    
    // Wiggle animation from notification tap
    if (params.get('wiggle') === 'true') {
      setWiggleFolder(true);
      setTimeout(() => setWiggleFolder(false), 1500);
    }
  }, [location.search, location.state]);

  // Apply pending story highlight when experiences are available
  useEffect(() => {
    const storyId = pendingStoryRef.current;
    if (!storyId || experiences.length === 0) return;

    const tryHighlight = () => {
      const el = document.querySelector(`[data-testid="story-card-${storyId}"]`);
      if (el) {
        // Use setTimeout to ensure DOM is fully rendered on mobile before scrolling
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        setHighlightedStoryId(storyId);
        if (pendingCommentsRef.current) setOpenCommentsId(storyId);
        setTimeout(() => setHighlightedStoryId(null), 3000);
        pendingStoryRef.current = null;
        pendingCommentsRef.current = false;
        return true;
      }
      return false;
    };

    // Try with longer delays for mobile - ensure DOM is fully rendered
    const t1 = setTimeout(() => {
      if (!tryHighlight()) {
        const t2 = setTimeout(() => {
          if (!tryHighlight()) {
            setTimeout(tryHighlight, 1000);
          }
        }, 600);
        return () => clearTimeout(t2);
      }
    }, 300);

    return () => clearTimeout(t1);
  }, [experiences]);

  const fetchData = async () => {
    try {
      if (isSharedMode) {
        // Shared folder mode - fetch from shared API
        const [storiesRes, foldersRes] = await Promise.all([
          axios.get(`${API}/folders/shared/${effectiveId}/stories`),
          axios.get(`${API}/folders`)
        ]);
        setExperiences(storiesRes.data);
        setFolders(foldersRes.data);
        // Cache stories for instant loading next time
        try { localStorage.setItem(`noyo_folder_stories_${effectiveId}`, JSON.stringify(storiesRes.data)); } catch {};
        
        // Get folder info from the shared stories response or a separate call
        const folderInfoRes = await axios.get(`${API}/folders/shared/${effectiveId}/info`).catch(() => null);
        if (folderInfoRes?.data) {
          setFolder(folderInfoRes.data);
        } else {
          // Fallback: construct from stories data
          setFolder({ id: effectiveId, name: storiesRes.data[0]?.topic_folder || 'Shared Folder', description: '' });
        }
        
        // Fetch comment counts + perspectives for shared folder stories
        if (storiesRes.data && storiesRes.data.length > 0) {
          try {
            const ids = storiesRes.data.map(e => e.id);
            const [countsRes, perspRes] = await Promise.all([
              axios.post(`${API}/stories/comment-counts`, { story_ids: ids }),
              axios.post(`${API}/stories/perspectives-batch`, { story_ids: ids }).catch(() => ({ data: {} }))
            ]);
            setCommentCounts(countsRes.data || {});
            setPerspectives(perspRes.data || {});
          } catch (e) {
            // Comment counts are optional - log but don't disrupt flow
          }
        }
      } else {
        // Regular mode
        const [expRes, foldersRes] = await Promise.all([
          axios.get(`${API}/experiences`),
          axios.get(`${API}/folders`)
        ]);
        let allExperiences = expRes.data;
        setFolders(foldersRes.data);
        
        if (effectiveId === 'unsorted') {
          setFolder({ id: 'unsorted', name: 'Unsorted Stories', description: 'Stories not yet sorted into a folder' });
        } else {
          const currentFolder = foldersRes.data.find(f => f.id === effectiveId);
          setFolder(currentFolder);
          setIsSharedWithMe(currentFolder?.name === 'Shared with Me');
          
          if (!currentFolder) {
            navigate('/stories');
            return;
          }
          
          // Check if this folder has collaborators - if so, merge their stories
          try {
            const sharedStoriesRes = await axios.get(`${API}/folders/shared/${effectiveId}/stories`);
            if (sharedStoriesRes.data && sharedStoriesRes.data.length > 0) {
              const myIds = new Set(allExperiences.map(e => e.id));
              const collabStories = sharedStoriesRes.data.filter(s => !myIds.has(s.id));
              allExperiences = [...allExperiences, ...collabStories];
              // Set hasCollaborators true if we got stories from collaborators
              setHasCollaborators(true);
            }
          } catch (e) {
            // No shared access or no collaborators - that's fine
          }
          
          // Also check if current user is owner with shares and get collaborator details
          try {
            const collabRes = await axios.get(`${API}/folders/${effectiveId}/collaborators`);
            if (collabRes.data) {
              setIsFolderOwner(collabRes.data.is_owner === true);
              if (collabRes.data.collaborators && collabRes.data.collaborators.length > 0) {
                setHasCollaborators(true);
                // Extract collaborator details for avatars (only accepted ones)
                const collabs = collabRes.data.collaborators
                  .filter(s => s.status === 'accepted')
                  .map(s => ({
                    share_id: s.id,
                    user_id: s.collaborator_id,
                    name: s.collaborator_name || s.collaborator_email?.split('@')[0] || 'User',
                    email: s.collaborator_email || '',
                    picture: s.collaborator_picture || null,
                    is_owner: false
                  }));
                setCollaboratorsList(collabs);
              }
            }
          } catch (e) {
            // Not owner or no shares - that's fine
          }
        }
        
        setExperiences(allExperiences);
        // Cache stories for instant loading next time
        try {
          const currentFolder = foldersRes.data.find(f => f.id === effectiveId);
          const folderStories = currentFolder ? allExperiences.filter(e => e.topic_folder === currentFolder.name) : allExperiences;
          localStorage.setItem(`noyo_folder_stories_${effectiveId}`, JSON.stringify(folderStories));
        } catch {};

        // Fetch comment counts + perspectives for all stories
        if (allExperiences.length > 0) {
          try {
            const ids = allExperiences.map(e => e.id);
            const [countsRes, perspRes] = await Promise.all([
              axios.post(`${API}/stories/comment-counts`, { story_ids: ids }),
              axios.post(`${API}/stories/perspectives-batch`, { story_ids: ids }).catch(() => ({ data: {} }))
            ]);
            setCommentCounts(countsRes.data || {});
            setPerspectives(perspRes.data || {});
          } catch (e) {
            // Comment counts are optional - log but don't disrupt flow
          }
        }
        const topicStories = allExperiences.filter(exp => {
          const currentFolder = foldersRes.data.find(f => f.id === effectiveId);
          return currentFolder && exp.topic_folder === currentFolder.name;
        });
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        const unviewedStoryIds = topicStories
          .filter(exp => new Date(exp.timestamp) > oneDayAgo && !exp.is_viewed && !exp.shared_from)
          .map(exp => exp.id);
        
        if (unviewedStoryIds.length > 0) {
          try {
            await axios.post(`${API}/experiences/mark-viewed`, {
              experience_ids: unviewedStoryIds
            });
          } catch (error) {
            console.error('Error marking stories as viewed:', error);
          }
        }
      }
      setIsLoadingFolder(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setIsLoadingFolder(false);
      if (isSharedMode && error?.response?.status === 403) {
        navigate('/stories');
      }
    }
  };

  // Folder tour disabled - empty array, tour will not show
  const FOLDER_TOUR_STEPS = [];

  // Note: Tour is disabled, so this effect does nothing
  useEffect(() => {
    // Tour disabled - do not show
  }, [folder, effectiveId, isSharedMode]);

  useEffect(() => {
    if (!showFolderTour) return;
    const updateRect = () => {
      const step = FOLDER_TOUR_STEPS[folderTourStep];
      if (!step) return;
      const el = document.querySelector(step.selector);
      if (el) {
        const rect = el.getBoundingClientRect();
        const pad = 8;
        setTourTargetRect({ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 });
      } else {
        setTourTargetRect(null);
      }
    };
    const t = setTimeout(updateRect, 100);
    window.addEventListener('resize', updateRect);
    return () => { clearTimeout(t); window.removeEventListener('resize', updateRect); };
  }, [showFolderTour, folderTourStep]);

  const advanceFolderTour = () => {
    if (folderTourStep < FOLDER_TOUR_STEPS.length - 1) {
      setFolderTourStep(folderTourStep + 1);
    } else {
      completeFolderTour();
    }
  };

  const completeFolderTour = () => {
    localStorage.setItem('noyo_folder_tour_done', 'true');
    setShowFolderTour(false);
    setFolderTourStep(0);
  };

  const topicExperiences = (isSharedMode
    ? experiences
    : experiences.filter(exp => exp.topic_folder === folder?.name)
  ).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const startEditExp = (exp) => {
    setEditingExpId(exp.id);
    setEditExpData({ summary: exp.summary, topic_folder: exp.topic_folder, images: exp.images || [] });
    setEditNewImages([]);
  };
  const [editNewImages, setEditNewImages] = useState([]); // [{file, preview}]
  const editImageInputRef = useRef(null);

  const handleRemoveEditImage = (idx) => {
    setEditExpData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  const handleAddEditImages = async (files) => {
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const uploadRes = await axios.post(`${API}/journal/upload-image`, formData);
        if (uploadRes.data.image) {
          setEditExpData(prev => ({ ...prev, images: [...(prev.images || []), uploadRes.data.image] }));
        }
      } catch (e) { console.error('Image upload error:', e); }
    }
  };

  const saveEditExp = async (id) => {
    try {
      const updatePayload = { summary: editExpData.summary, topic_folder: editExpData.topic_folder, images: editExpData.images };
      await axios.put(`${API}/experiences/${id}`, updatePayload);
      setEditingExpId(null);
      setEditNewImages([]);
      await fetchData();
    } catch (error) {
      console.error('Error updating experience:', error);
    }
  };

  // ── Perspective handlers ──────────────────────────────────────────────
  const handleAddPerspective = async (storyId) => {
    const text = (perspectiveInput[storyId] || '').trim();
    if (!text) return;
    setSubmittingPerspective(storyId);
    try {
      const res = await axios.post(`${API}/stories/${storyId}/perspectives`, { content: text });
      setPerspectives(prev => ({ ...prev, [storyId]: [...(prev[storyId] || []), res.data] }));
      setPerspectiveInput(prev => ({ ...prev, [storyId]: '' }));
    } catch (err) { console.error('Add perspective failed:', err); }
    finally { setSubmittingPerspective(null); }
  };

  const handleEditPerspective = async (storyId, perspId) => {
    const text = editPerspectiveContent.trim();
    if (!text) return;
    try {
      await axios.put(`${API}/stories/${storyId}/perspectives/${perspId}`, { content: text });
      setPerspectives(prev => ({
        ...prev,
        [storyId]: (prev[storyId] || []).map(p => p.id === perspId ? { ...p, content: text, edited_at: new Date().toISOString() } : p)
      }));
      setEditingPerspectiveId(null);
      setEditPerspectiveContent('');
    } catch (err) { console.error('Edit perspective failed:', err); }
  };

  const handleDeletePerspective = async (storyId, perspId) => {
    try {
      await axios.delete(`${API}/stories/${storyId}/perspectives/${perspId}`);
      setPerspectives(prev => ({
        ...prev,
        [storyId]: (prev[storyId] || []).filter(p => p.id !== perspId)
      }));
    } catch (err) { console.error('Delete perspective failed:', err); }
  };

  const handleToggleAiSummary = async (expId) => {
    if (showAiSummary[expId]) {
      setShowAiSummary(prev => ({ ...prev, [expId]: false }));
      return;
    }
    const exp = experiences.find(e => e.id === expId);
    if (exp?.ai_summary) {
      setShowAiSummary(prev => ({ ...prev, [expId]: true }));
      return;
    }
    // Generate on demand
    setGeneratingSummary(prev => ({ ...prev, [expId]: true }));
    try {
      const res = await axios.post(`${API}/experiences/${expId}/summarize`);
      setExperiences(prev => prev.map(e => e.id === expId ? { ...e, ai_summary: res.data.ai_summary } : e));
      setShowAiSummary(prev => ({ ...prev, [expId]: true }));
    } catch (err) {
      console.error('Summarize error:', err);
    } finally {
      setGeneratingSummary(prev => ({ ...prev, [expId]: false }));
    }
  };

  const handleAddImageToStory = async (storyId, file) => {
    if (!file) return;
    setUploadingImageForStory(storyId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await axios.post(`${API}/journal/upload-image`, formData);
      if (uploadRes.data.image) {
        const story = experiences.find(e => e.id === storyId);
        const existingImages = story?.images || [];
        const updatedImages = [...existingImages, uploadRes.data.image];
        await axios.put(`${API}/experiences/${storyId}`, { images: updatedImages });
        setExperiences(prev => prev.map(e => e.id === storyId ? { ...e, images: updatedImages } : e));
      }
    } catch (error) {
      console.error('Error adding image to story:', error);
    } finally {
      setUploadingImageForStory(null);
    }
  };

  const createManualStory = async () => {
    if (!newStorySummary.trim()) return;
    try {
      const payload = {
        summary: newStorySummary,
        topic_folder: folder.name
      };
      // If in shared mode (user is collaborator), include shared_folder_id
      if (isSharedMode && folder.id) {
        payload.shared_folder_id = folder.id;
      }
      await axios.post(`${API}/experiences`, payload);
      setNewStorySummary('');
      setShowAddStory(false);
      await fetchData();
    } catch (error) {
      console.error('Error creating story:', error);
    }
  };

  const handleSearchStories = async () => {
    if (isSharedMode) return;
    setIsSearchingStories(true);
    try {
      const response = await axios.post(`${API}/folders/${effectiveId}/search-stories`);
      const storiesFound = response.data.stories_extracted || 0;
      if (storiesFound > 0) {
        alert(`Found ${storiesFound} new ${storiesFound === 1 ? 'story' : 'stories'}!`);
      } else {
        alert('No new stories found in your journal entries.');
      }
      await fetchData();
    } catch (error) {
      console.error('Error searching stories:', error);
      alert('Failed to search for stories. Please try again.');
    } finally {
      setIsSearchingStories(false);
    }
  };

  const handleSaveDescription = async () => {
    if (isSharedMode) return;
    try {
      await axios.put(`${API}/folders/${effectiveId}/description`, { description: editDescription });
      setShowEditDescription(false);
      await fetchData();
    } catch (error) {
      console.error('Error updating description:', error);
      alert('Failed to update description. Please try again.');
    }
  };

  const startEditDescription = () => {
    setEditDescription(folder?.description || '');
    setShowEditDescription(true);
  };

  const startEditTitle = () => {
    setEditTitle(folder?.name || '');
    setShowEditTitle(true);
  };

  const handleSaveTitle = async () => {
    if (!editTitle.trim() || isSharedMode) return;
    try {
      await axios.put(`${API}/folders/${effectiveId}`, { name: editTitle.trim() });
      setShowEditTitle(false);
      await fetchData();
    } catch (error) {
      console.error('Error updating title:', error);
      alert('Failed to update title. Please try again.');
    }
  };

  const handleDeleteExp = async (id) => {
    try {
      await axios.delete(`${API}/experiences/${id}`);
      setDeleteExpConfirm(null);
      await fetchData();
    } catch (error) {
      console.error('Error deleting experience:', error);
    }
  };

  const handleDeleteFolder = async () => {
    if (isSharedMode) return;
    try {
      await axios.delete(`${API}/folders/${effectiveId}`);
      navigate('/stories');
    } catch (error) {
      console.error('Error deleting folder:', error);
    }
  };

  // --- Inline share helpers ---
  const BASE_URL = typeof window !== 'undefined' ? window.location.origin : '';

  const fetchShareLink = async () => {
    try {
      const res = await axios.get(`${API}/folders/${effectiveId}/share-link`);
      if (res.data.has_link) setShareLink(res.data);
    } catch {}
  };

  const createShareLink = async () => {
    setShareLinkLoading(true);
    try {
      const res = await axios.post(`${API}/folders/${effectiveId}/share-link`);
      setShareLink(res.data);
    } catch (err) { console.error(err); }
    finally { setShareLinkLoading(false); }
  };

  const copyShareLink = () => {
    if (!shareLink) return;
    const url = `${BASE_URL}/invite/${shareLink.link_id}`;
    navigator.clipboard.writeText(url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const searchUsers = async (q) => {
    setInviteEmail(q);
    if (q.length < 3) { setInviteSearchResults([]); return; }
    try {
      const res = await axios.get(`${API}/users/search?q=${encodeURIComponent(q)}`);
      setInviteSearchResults(res.data || []);
    } catch { setInviteSearchResults([]); }
  };

  const sendInvite = async (email) => {
    setInviteLoading(true);
    try {
      await axios.post(`${API}/folders/${effectiveId}/share`, { email });
      setInviteSuccess(`Invite sent to ${email}`);
      setInviteEmail('');
      setInviteSearchResults([]);
      // Save to recent
      const updated = [email, ...recentCollabs.filter(e => e !== email)].slice(0, 10);
      setRecentCollabs(updated);
      try { localStorage.setItem('noyo_recent_collabs', JSON.stringify(updated)); } catch {}
      await fetchData();
      setTimeout(() => setInviteSuccess(''), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send invite');
    } finally { setInviteLoading(false); }
  };

  // --- Inline find helpers ---
  const handleFindStories = async () => {
    setFindLoading(true);
    setPendingStories([]);
    setFindSearched(false);
    try {
      const res = await axios.post(`${API}/folders/${effectiveId}/search-stories`);
      setPendingStories(res.data.pending_stories || []);
    } catch (err) { console.error(err); }
    finally { setFindLoading(false); setFindSearched(true); }
  };

  const confirmStory = async (story, idx) => {
    try {
      await axios.post(`${API}/experiences/publish`, { stories: [story] });
      setPendingStories(prev => prev.filter((_, i) => i !== idx));
      await fetchData();
    } catch (err) { console.error(err); }
  };

  const rejectStory = (idx) => {
    setPendingStories(prev => prev.filter((_, i) => i !== idx));
  };

  // --- Edit in-place helpers ---
  const startEditInPlace = () => {
    setEditName(folder?.name || '');
    setEditDesc(folder?.description || '');
    setIsEditingFolder(true);
    setShowFolderMenu(false);
  };

  const saveEditInPlace = async () => {
    try {
      if (editName.trim() && editName.trim() !== folder?.name) {
        await axios.put(`${API}/folders/${effectiveId}`, { name: editName.trim() });
      }
      const newDesc = editDesc.trim();
      if (newDesc !== (folder?.description || '')) {
        await axios.put(`${API}/folders/${effectiveId}/description`, { description: newDesc });
      }
      setActiveSection(null);
      setIsEditingFolder(false);
      setIsEditingTitle(false);
      setIsEditingDesc(false);
      await fetchData();
    } catch (err) {
      console.error(err);
      alert('Failed to save changes');
    }
  };

  const cancelEditInPlace = () => {
    setActiveSection(null);
    setIsEditingFolder(false);
    setIsEditingTitle(false);
    setIsEditingDesc(false);
  };

  const handleColorChange = async (hex) => {
    // Instant local update for responsiveness
    setFolder(prev => ({ ...prev, color: hex }));
    try {
      await axios.put(`${API}/folders/${effectiveId}/color`, { color: hex });
    } catch (err) {
      console.error(err);
      // Revert on error
      setFolder(prev => ({ ...prev, color: folder?.color }));
    }
  };

  const handleToggleMemberSharing = async () => {
    const newVal = !folder?.allow_member_sharing;
    try {
      await axios.put(`${API}/folders/${effectiveId}/permissions`, { allow_member_sharing: newVal });
      setFolder(prev => ({ ...prev, allow_member_sharing: newVal }));
    } catch (err) { console.error(err); }
  };

  const handleRemoveCollaborator = async (shareId, collabName) => {
    if (!window.confirm(`Remove ${collabName} from this folder?`)) return;
    try {
      await axios.delete(`${API}/folders/share/${shareId}`);
      setCollaboratorsList(prev => prev.filter(c => c.share_id !== shareId));
    } catch (err) { console.error('Remove collaborator failed:', err); }
  };

  const handleAddToFolder = async (expId, targetFolderName) => {
    setAddingToFolder(expId + targetFolderName);
    try {
      const exp = experiences.find(e => e.id === expId);
      if (!exp) return;
      const payload = {
        summary: exp.summary,
        topic_folder: targetFolderName,
        images: exp.images || []
      };
      // Preserve live-link when copying a shared story
      if (exp.original_story_id) {
        payload.original_story_id = exp.original_story_id;
        payload.shared_from = exp.shared_from;
        payload.shared_from_email = exp.shared_from_email;
        if (exp.ai_summary) payload.ai_summary = exp.ai_summary;
      }
      await axios.post(`${API}/experiences`, payload);
      await fetchData();
      setAddToFolderOpen(null);
    } catch (error) {
      console.error('Error adding to folder:', error);
    } finally {
      setAddingToFolder(null);
    }
  };

  const handleQuickStory = async () => {
    if ((!quickStory.trim() && quickImages.length === 0 && !quickAudioBlob) || !folder) return;
    
    // Only analyze text if there's text content
    if (quickStory.trim()) {
      setIsAnalyzing(true);
      try {
        const analyzeRes = await axios.post(`${API}/experiences/analyze-content`, { text: quickStory });
        if (analyzeRes.data.is_multiple) {
          setStoryCount(analyzeRes.data.count || 2);
          setShowJournalPrompt(true);
          setIsAnalyzing(false);
          return;
        }
      } catch (err) {
        console.error('Analyze error:', err);
      }
      setIsAnalyzing(false);
    }
    
    await saveAsQuickStory();
  };

  const saveAsQuickStory = async () => {
    setIsAddingQuickStory(true);
    setIsTopicSaving(true);
    setTopicSaveProgress(0);
    setShowJournalPrompt(false);
    try {
      const totalSteps = quickImages.length + 2;
      let done = 0;
      const tick = () => { done++; setTopicSaveProgress(Math.round((done / totalSteps) * 100)); };

      let imageData = [];
      for (const img of quickImages) {
        try {
          const formData = new FormData();
          formData.append('file', img.file);
          const uploadRes = await axios.post(`${API}/journal/upload-image`, formData);
          if (uploadRes.data.image) imageData.push(uploadRes.data.image);
        } catch (e) { console.error('Image upload error:', e); }
        tick();
      }
      
      const hasContent = quickStory.trim();
      const fallback = imageData.length > 0 ? '(image)' : '(audio)';
      
      if (isSharedMode) {
        await axios.post(`${API}/folders/shared/${effectiveId}/add-story`, {
          summary: hasContent ? quickStory : fallback, images: imageData
        });
      } else {
        await axios.post(`${API}/experiences`, {
          summary: hasContent ? quickStory : fallback, topic_folder: folder.name, images: imageData
        });
        try {
          const autoRes = await axios.post(`${API}/experiences/auto-sort`, { summary: quickStory });
          const autoSorted = autoRes.data.sorted_to || [];
          const newAutoFolders = autoSorted.filter(f => f !== folder.name);
          if (newAutoFolders.length > 0) {
            setAutoSortToast(`AI also added to: ${newAutoFolders.join(', ')} (review pending)`);
            setTimeout(() => setAutoSortToast(null), 4000);
          }
        } catch (sortErr) { console.error('Auto-sort error:', sortErr); }
      }
      tick();

      setQuickStory('');
      quickImages.forEach(img => URL.revokeObjectURL(img.preview));
      setQuickImages([]);
      setQuickImage(null);
      tick();
      await fetchData();
    } catch (error) {
      console.error('Error creating story:', error);
    } finally {
      setIsAddingQuickStory(false);
      setIsTopicSaving(false);
      setTopicSaveProgress(0);
    }
  };

  const saveAsJournalEntry = async () => {
    setIsAddingQuickStory(true);
    setShowJournalPrompt(false);
    try {
      await axios.post(`${API}/journal`, { content: quickStory });
      setQuickStory('');
      await fetchData();
      setAutoSortToast('Saved as journal entry — AI is extracting stories');
      setTimeout(() => setAutoSortToast(null), 4000);
    } catch (error) {
      console.error('Error creating journal entry:', error);
    } finally {
      setIsAddingQuickStory(false);
    }
  };

  const startQuickRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      quickStreamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      quickMediaRecorderRef.current = mediaRecorder;
      quickAudioChunksRef.current = [];
      setRecordingDuration(0);
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) quickAudioChunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = async () => {
        clearInterval(recordingTimerRef.current);
        const audioBlob = new Blob(quickAudioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(t => t.stop());
        setQuickAudioBlob(audioBlob);
        setIsTranscribingQuick(true);
        setTranscriptionProgress(0);
        
        // Simulate progress for better UX
        const progressInterval = setInterval(() => {
          setTranscriptionProgress(prev => prev >= 90 ? prev : prev + Math.random() * 15);
        }, 500);
        
        try {
          const formData = new FormData();
          formData.append('audio', audioBlob, 'quick_recording.webm');
          const res = await axios.post(`${API}/audio/transcribe`, formData);
          setTranscriptionProgress(100);
          if (res.data.text) {
            setQuickStory(prev => prev ? prev + '\n' + res.data.text : res.data.text);
          }
        } catch (err) {
          console.error('Transcription error:', err);
        } finally {
          clearInterval(progressInterval);
          setIsTranscribingQuick(false);
          setTranscriptionProgress(0);
        }
      };
      mediaRecorder.start();
      setIsRecordingQuick(true);
      setGlobalRecording(true); // Disable swipe navigation
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Mic access denied:', err);
    }
  };

  const stopQuickRecording = () => {
    if (quickMediaRecorderRef.current && isRecordingQuick) {
      quickMediaRecorderRef.current.stop();
      setIsRecordingQuick(false);
      setIsPausedQuick(false);
      setGlobalRecording(false); // Re-enable swipe navigation
    }
  };

  const pauseQuickRecording = () => {
    if (quickMediaRecorderRef.current && isRecordingQuick && !isPausedQuick) {
      quickMediaRecorderRef.current.pause();
      setIsPausedQuick(true);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const resumeQuickRecording = () => {
    if (quickMediaRecorderRef.current && isRecordingQuick && isPausedQuick) {
      quickMediaRecorderRef.current.resume();
      setIsPausedQuick(false);
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    }
  };

  const formatDuration = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (!folder) return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-12">
          <div className="flex items-center space-x-2 text-muted-foreground mb-6">
            <ChevronRight className="w-4 h-4 rotate-180" />
            <span>Back to Stories</span>
          </div>
          <div className="animate-pulse space-y-4">
            <div className="h-10 w-2/3 bg-secondary rounded-lg" />
            <div className="h-4 w-1/3 bg-secondary/60 rounded" />
            <div className="h-4 w-24 bg-secondary/40 rounded" />
          </div>
        </div>
        <div className="max-w-lg mb-6">
          <div className="animate-pulse rounded-2xl border border-border bg-card p-4 h-32" />
        </div>
        <div className="columns-1 lg:columns-2 xl:columns-3 gap-4 space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse bg-card border border-border rounded-2xl p-5 h-40" />
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition} className="min-h-screen pt-24 pb-12" style={folder?.color ? { '--folder-color': folder.color } : {}}>
      {/* Subtle color gradient at top when folder has a color */}
      {folder?.color && (
        <div className="fixed top-0 left-0 right-0 h-72 pointer-events-none z-0" style={{ background: `linear-gradient(to bottom, ${hexToRgba(folder.color, 0.15)}, ${hexToRgba(folder.color, 0.06)} 50%, transparent)` }} />
      )}
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="mb-12">
          <button onClick={() => navigate('/stories')} className="flex items-center space-x-2 transition-colors mb-6" style={folder?.color ? { color: folder.color } : {}} data-testid="back-to-stories-button" title="Back to Stories">
            <ChevronRight className="w-4 h-4 rotate-180" />
            <span>Back to Stories</span>
          </button>
          <div className="flex flex-col gap-3">
            {(
              /* Normal display mode — title and description with inline edit */
              <>
                <div className="flex items-start gap-2 mb-1">
                  {isEditingTitle ? (
                    <div className="flex-1">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape') { setIsEditingTitle(false); setIsEditingDesc(false); } }}
                        autoFocus
                        className="text-2xl sm:text-3xl font-serif font-medium bg-transparent border-b-2 border-primary focus:outline-none w-full min-w-0"
                        placeholder="Folder name"
                      />
                    </div>
                  ) : (
                    <>
                      <h1 className={`text-3xl sm:text-4xl md:text-5xl font-serif font-medium tracking-tight text-foreground break-words ${wiggleFolder ? 'animate-wiggle' : ''}`} data-testid={`topic-title-${folder.id}`}>{folder.name}</h1>
                      {!isSharedMode && !isSharedWithMe && effectiveId !== 'unsorted' && (
                        <button
                          onClick={() => { setEditName(folder?.name || ''); setEditDesc(folder?.description || ''); setIsEditingTitle(true); setIsEditingDesc(true); }}
                          className="text-muted-foreground/40 hover:text-primary transition-colors p-1 flex-shrink-0 mt-2"
                          style={folder?.color ? { color: hexToRgba(folder.color, 0.5) } : {}}
                          title="Edit folder"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </>
                  )}
                  {effectiveId !== 'unsorted' && !isEditingTitle && (
                    <button
                      onClick={() => setShowFolderMenu(true)}
                      className="transition-colors p-2 rounded-xl flex-shrink-0 border mt-1"
                      style={folder?.color ? { color: folder.color, borderColor: hexToRgba(folder.color, 0.3) } : {}}
                      data-testid="folder-menu-button"
                      title="Folder settings"
                    >
                      <MoreHorizontal className="w-6 h-6" />
                    </button>
                  )}
                </div>
                {/* Shared info row — icon, shared by, collab avatars, story count */}
                {isSharedMode && folder.owner_name && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Users className="w-4 h-4 text-primary flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">Shared by {folder.owner_name}</p>
                    {collaboratorsList.length > 0 && (
                      <div className="flex items-center">
                        {collaboratorsList.slice(0, 4).map((collab, idx) => (
                          <div
                            key={collab.user_id || idx}
                            className="w-6 h-6 rounded-full border-2 border-zinc-200 dark:border-zinc-800 bg-primary/20 text-primary flex items-center justify-center text-[10px] font-medium overflow-hidden"
                            style={{ marginLeft: idx === 0 ? 0 : '-8px', zIndex: 4 - idx }}
                            title={collab.name}
                          >
                            {collab.picture ? (
                              <img src={collab.picture} alt="" className="w-full h-full object-cover" />
                            ) : (
                              collab.name?.charAt(0)?.toUpperCase() || '?'
                            )}
                          </div>
                        ))}
                        {collaboratorsList.length > 4 && (
                          <div className="w-6 h-6 rounded-full border-2 border-zinc-200 dark:border-zinc-800 bg-primary/10 text-primary flex items-center justify-center text-[10px] font-medium" style={{ marginLeft: '-8px', zIndex: 0 }}>
                            +{collaboratorsList.length - 4}
                          </div>
                        )}
                      </div>
                    )}
                    <span className="text-sm text-muted-foreground">&middot; {topicExperiences.length} {topicExperiences.length === 1 ? 'story' : 'stories'}</span>
                  </div>
                )}
                {isEditingDesc ? (
                  <div className="mt-1">
                    <textarea
                      value={editDesc}
                      onChange={(e) => setEditDesc(e.target.value)}
                      rows={2}
                      className="w-full text-sm bg-transparent border-b-2 border-primary focus:outline-none resize-none min-h-[40px]"
                      placeholder="Add a description to help AI find stories..."
                    />
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={saveEditInPlace} className="text-white rounded-lg px-4 py-1.5 text-sm font-medium transition-colors flex items-center gap-1.5" style={{ backgroundColor: folder?.color || '#8DA399' }}><Check className="w-3.5 h-3.5" />Save</button>
                      <button onClick={() => { setIsEditingTitle(false); setIsEditingDesc(false); }} className="text-muted-foreground hover:text-foreground rounded-lg px-4 py-1.5 text-sm transition-colors">Cancel</button>
                    </div>
                  </div>
                ) : (
                  folder.description && (
                    <p className="text-sm italic mt-1" style={folder?.color ? { color: hexToRgba(folder.color, 0.8) } : {}} data-testid="folder-description-display">"{folder.description}"</p>
                  )
                )}
              </>
            )}
            {!isSharedMode && collaboratorsList.length > 0 && (
              <div className="flex items-center mt-1">
                {collaboratorsList.slice(0, 4).map((collab, idx) => (
                  <div
                    key={collab.user_id || idx}
                    className="w-6 h-6 rounded-full border-2 border-zinc-200 dark:border-zinc-800 bg-primary/20 text-primary flex items-center justify-center text-[10px] font-medium overflow-hidden"
                    style={{ marginLeft: idx === 0 ? 0 : '-8px', zIndex: 4 - idx }}
                    title={collab.name}
                  >
                    {collab.picture ? (
                      <img src={collab.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      collab.name?.charAt(0)?.toUpperCase() || '?'
                    )}
                  </div>
                ))}
                {collaboratorsList.length > 4 && (
                  <div className="w-6 h-6 rounded-full border-2 border-zinc-200 dark:border-zinc-800 bg-primary/10 text-primary flex items-center justify-center text-[10px] font-medium" style={{ marginLeft: '-8px', zIndex: 0 }}>
                    +{collaboratorsList.length - 4}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Quick Add Story Box - now available for collaborators in shared mode */}
        {!isSharedWithMe && (
        <div className="max-w-lg mb-6" data-testid="topic-quick-story-box">
          <div className="bg-card border-2 border-dashed rounded-xl p-5 transition-colors" style={folder?.color ? { borderColor: hexToRgba(folder.color, 0.55) } : { borderColor: 'var(--color-primary-30, rgba(138, 163, 153, 0.3))' }}>
            <div className="relative">
              <textarea value={quickStory} onChange={(e) => setQuickStory(e.target.value)} placeholder={isTranscribingQuick ? "Transcribing audio..." : `Write a new story for ${folder.name}...`} disabled={isTranscribingQuick} className="w-full bg-transparent border-none text-base md:text-lg font-serif leading-relaxed focus:outline-none resize-none min-h-[80px] max-h-[150px] placeholder:text-muted-foreground/40 disabled:opacity-50 pr-14" rows={4} data-testid="topic-quick-story-textarea" />
              <div className="absolute bottom-1 right-1 flex items-center space-x-1">
                {isRecordingQuick && <span className={`text-xs font-mono font-medium tabular-nums ${isPausedQuick ? 'text-orange-500' : 'text-red-500'}`} data-testid="topic-recording-timer">{isPausedQuick ? 'Paused ' : ''}{formatDuration(recordingDuration)}</span>}
                {isRecordingQuick ? (
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={isPausedQuick ? resumeQuickRecording : pauseQuickRecording}
                      className={`p-2 rounded-full ${isPausedQuick ? 'bg-accent text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'} transition-colors`}
                      data-testid="topic-quick-pause-record"
                      title={isPausedQuick ? "Resume recording" : "Pause recording"}
                    >
                      {isPausedQuick ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                    </button>
                    <button onClick={stopQuickRecording} className="p-2.5 rounded-full bg-red-500 text-white animate-pulse" data-testid="topic-quick-stop-record" title="Stop recording"><Square className="w-5 h-5" /></button>
                  </div>
                ) : isTranscribingQuick ? (
                  <ProgressBar progress={transcriptionProgress} label="Transcribing" variant="accent" />
                ) : (
                  <>
                    <label className="p-2.5 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors cursor-pointer" title="Upload images" data-testid="topic-quick-image-upload">
                      <ImagePlus className="w-5 h-5" />
                      <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        const remaining = 10 - quickImages.length;
                        const toAdd = files.slice(0, remaining).map(file => ({
                          file,
                          preview: URL.createObjectURL(file)
                        }));
                        if (toAdd.length > 0) setQuickImages(prev => [...prev, ...toAdd]);
                        e.target.value = '';
                      }} />
                    </label>
                    <button onClick={startQuickRecording} className="p-2.5 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors" data-testid="topic-quick-record" title="Record audio"><Mic className="w-5 h-5" /></button>
                  </>
                )}
              </div>
            </div>
            {quickAudioBlob && !isRecordingQuick && !isTranscribingQuick && (
              <div className="flex items-center space-x-2 pt-1 pb-1">
                <span className="text-xs text-accent flex items-center space-x-1"><Mic className="w-3 h-3" /><span>Audio attached</span></span>
                <button onClick={() => setQuickAudioBlob(null)} className="text-xs text-muted-foreground hover:text-red-400 transition-colors" title="Remove audio"><X className="w-3 h-3" /></button>
              </div>
            )}
            {quickImages.length > 0 && (
              <div className="flex items-center gap-2 pt-2 pb-1 flex-wrap" data-testid="topic-quick-images-preview">
                {quickImages.map((img, idx) => (
                  <div key={img.preview || `img-${idx}`} className="relative group w-14 h-14 rounded-lg overflow-hidden border border-border">
                    <img src={img.preview} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => {
                        URL.revokeObjectURL(img.preview);
                        setQuickImages(prev => prev.filter((_, i) => i !== idx));
                      }}
                      className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`topic-remove-quick-image-${idx}`}
                      title="Remove image"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
                {quickImages.length < 10 && (
                  <label className="w-14 h-14 rounded-lg border-2 border-dashed border-border hover:border-primary/50 flex items-center justify-center text-muted-foreground hover:text-primary transition-colors cursor-pointer" title="Add more images">
                    <ImagePlus className="w-4 h-4" />
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      const remaining = 10 - quickImages.length;
                      const toAdd = files.slice(0, remaining).map(file => ({
                        file,
                        preview: URL.createObjectURL(file)
                      }));
                      if (toAdd.length > 0) setQuickImages(prev => [...prev, ...toAdd]);
                      e.target.value = '';
                    }} />
                  </label>
                )}
              </div>
            )}
            {/* Save progress bar */}
            {isTopicSaving && (
              <div className="pt-2" data-testid="topic-save-progress">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-muted/30 rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${topicSaveProgress}%` }} />
                  </div>
                  <span className="text-xs font-bold text-muted-foreground tabular-nums">{topicSaveProgress}%</span>
                </div>
              </div>
            )}
            {(quickStory.trim() || quickAudioBlob || quickImages.length > 0) && !isTopicSaving && (
              <div className="flex justify-end pt-2 border-t border-border">
                <button onClick={handleQuickStory} disabled={isAddingQuickStory || isAnalyzing} className="text-xs text-white rounded-full px-4 py-2 font-medium transition-colors disabled:opacity-50 flex items-center space-x-1" style={{ backgroundColor: folder?.color || '#8DA399' }} data-testid="topic-quick-story-save" title="Save Story">
                  {isAnalyzing ? <Sparkles className="w-4 h-4 animate-spin" /> : isAddingQuickStory ? <Sparkles className="w-4 h-4 animate-pulse" /> : <Save className="w-4 h-4" />}<span>{isAnalyzing ? 'Checking...' : 'Add Story'}</span>
                </button>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Auto-sort toast */}
        {autoSortToast && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-accent text-white px-6 py-3 rounded-full shadow-lg z-50 text-sm font-medium" data-testid="auto-sort-toast">{autoSortToast}</motion.div>
        )}

        {showJournalPrompt && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="journal-prompt-modal">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-card-solid rounded-2xl p-6 max-w-md w-full shadow-2xl">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-accent/10 rounded-xl"><Sparkles className="w-5 h-5 text-accent" /></div>
                <h3 className="text-lg font-serif font-medium text-foreground">Multiple stories detected</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                This looks like it contains <span className="font-medium text-foreground">{storyCount} different stories</span>. Would you like to save it as a journal entry and let AI extract each story into the right folders?
              </p>
              <div className="space-y-3">
                <button onClick={saveAsJournalEntry} disabled={isAddingQuickStory} className="w-full bg-primary text-white hover:bg-primary/80 rounded-full px-5 py-3 font-medium transition-all flex items-center justify-center space-x-2 disabled:opacity-50" data-testid="save-as-journal-button">
                  <BookOpen className="w-4 h-4" /><span>{isAddingQuickStory ? 'Saving...' : 'Save as journal entry'}</span>
                </button>
                <button onClick={saveAsQuickStory} disabled={isAddingQuickStory} className="w-full bg-card border border-border text-foreground hover:bg-secondary rounded-full px-5 py-3 font-medium transition-all disabled:opacity-50" data-testid="save-as-single-story-button">
                  Save as single story anyway
                </button>
                <button onClick={() => setShowJournalPrompt(false)} className="w-full text-muted-foreground hover:text-foreground text-sm py-1 transition-colors" data-testid="cancel-journal-prompt">Cancel</button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {showAddStory && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-2xl p-6 mb-8" data-testid="add-story-form">
            <h3 className="text-lg font-serif font-medium mb-3">Add New Story to {folder.name}</h3>
            <textarea value={newStorySummary} onChange={(e) => setNewStorySummary(e.target.value)} placeholder="Write your story here..." className="w-full bg-card border-border focus:border-primary focus:ring-primary/20 rounded-xl px-4 py-3 text-base min-h-[120px] resize-none" data-testid="new-story-textarea" />
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setShowAddStory(false)} className="bg-transparent text-foreground hover:bg-secondary rounded-full px-6 py-2 transition-colors" data-testid="cancel-add-story-button">Cancel</button>
              <button onClick={createManualStory} className="bg-primary text-white hover:bg-primary/80 rounded-full px-6 py-2 transition-colors" data-testid="save-manual-story-button">Add Story</button>
            </div>
          </motion.div>
        )}

        {topicExperiences.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: folder?.color ? hexToRgba(folder.color, 0.1) : 'var(--color-primary-10, rgba(138, 163, 153, 0.1))' }}>
              <FolderOpen className="w-8 h-8" style={{ color: folder?.color ? hexToRgba(folder.color, 0.4) : 'var(--color-primary-40)' }} />
            </div>
            <h3 className="font-serif font-medium text-foreground mb-2">No stories yet</h3>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto mb-4">
              {effectiveId === 'unsorted'
                ? 'Stories that AI can\'t categorize will appear here.'
                : 'Add a story above, or write a journal entry and AI will sort relevant stories into this folder.'}
            </p>
            {effectiveId !== 'unsorted' && (
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 text-sm text-primary font-medium hover:underline"
              >
                <BookOpen className="w-4 h-4" />
                Write a journal entry
              </button>
            )}
          </div>
        ) : (
          <div className="columns-1 lg:columns-2 xl:columns-3 gap-6 space-y-6" data-testid="topic-stories-list">
            {topicExperiences.map((exp) => {
              const isMine = exp.is_mine !== undefined ? exp.is_mine : true;
              return (
              <SwipeToDelete 
                key={exp.id} 
                onDelete={() => setDeleteExpConfirm(exp.id)}
                disabled={!isMine || isSharedWithMe || editingExpId === exp.id}
              >
              <motion.div 
                whileHover={{ y: -4 }} 
                animate={highlightedStoryId === exp.id ? { x: [0, -4, 4, -4, 4, 0] } : {}}
                transition={highlightedStoryId === exp.id ? { duration: 0.5, repeat: 2 } : { type: 'spring', stiffness: 300 }}
                className={`p-4 rounded-2xl bg-card border-2 transition-all duration-300 hover:shadow-lg group relative break-inside-avoid ${addToFolderOpen === exp.id ? 'z-[101]' : ''} ${highlightedStoryId === exp.id ? 'border-primary ring-2 ring-primary/30 shadow-lg shadow-primary/20' : isSharedWithMe ? 'border-accent/60 hover:border-accent/80' : folder?.color ? '' : 'border-border hover:border-primary/50'}`}
                style={!highlightedStoryId && !isSharedWithMe && folder?.color ? { borderColor: hexToRgba(folder.color, 0.45) } : {}}
                data-testid={`story-card-${exp.id}`}>
                {/* Top-right badges */}
                {(() => {
                  const storyDate = new Date(exp.timestamp);
                  const now = new Date();
                  const diffHours = (now - storyDate) / (1000 * 60 * 60);
                  const isNew = diffHours <= 24 && !exp.is_viewed;
                  const showSortBadge = !isSharedMode && effectiveId === 'unsorted' && isMine;
                  return (
                    <>
                      {isNew && <span className="absolute -top-2 -right-2 z-30 bg-accent text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider shadow-md pulse-slow" data-testid={`new-badge-${exp.id}`}>New</span>}
                      {showSortBadge && (
                        <button
                          onClick={() => {
                            if (addToFolderOpen === exp.id) { setAddToFolderOpen(null); return; }
                            const storyFolders = new Set(experiences.filter(e => e.summary === exp.summary).map(e => e.topic_folder));
                            setInitialSelections(new Set(storyFolders));
                            setPendingSelections(new Set(storyFolders));
                            setPendingNewFolders([]);
                            setInlineFolderName('');
                            setAddToFolderOpen(exp.id);
                          }}
                          className={`absolute ${isNew ? '-top-2 right-12' : '-top-2 -right-2'} z-30 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider shadow-md cursor-pointer transition-colors ${addToFolderOpen === exp.id ? 'bg-primary text-white' : 'bg-amber-600 text-white hover:bg-amber-700'}`}
                          data-testid={`sort-badge-${exp.id}`}
                        >
                          Sort
                        </button>
                      )}
                    </>
                  );
                })()}
                {editingExpId === exp.id && isMine ? (
                  <div>
                    <textarea value={editExpData.summary} onChange={(e) => setEditExpData({ ...editExpData, summary: e.target.value })} className="w-full bg-transparent border border-input rounded-xl p-3 text-sm leading-relaxed focus:outline-none focus:border-primary min-h-[100px]" data-testid={`edit-story-textarea-${exp.id}`} />
                    {/* Image management in edit mode */}
                    {(editExpData.images?.length > 0) && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {editExpData.images.map((img, idx) => (
                          <div key={img.id || img.filename || idx} className="relative group w-16 h-16 rounded-lg overflow-hidden border border-border">
                            <img src={`${API}/journal/images/${img.id || img.filename}?variant=thumb`} alt="" className="w-full h-full object-cover" />
                            <button
                              onClick={() => handleRemoveEditImage(idx)}
                              className="absolute top-0.5 right-0.5 bg-black/70 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Remove image"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex justify-between space-x-2 mt-3">
                      <div className="flex items-center space-x-2">
                        <button onClick={() => { setEditingExpId(null); setDeleteExpConfirm(exp.id); }} className="text-xs text-red-500 hover:text-red-600 hover:bg-red-500/10 rounded-full px-4 py-1 transition-colors flex items-center space-x-1" data-testid={`delete-from-edit-story-${exp.id}`}><Trash2 className="w-4 h-4" /><span>Delete</span></button>
                        <label className="text-xs text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-full px-3 py-1 transition-colors flex items-center space-x-1 cursor-pointer" title="Add images">
                          <ImagePlus className="w-4 h-4" /><span>Add Image</span>
                          <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { const files = Array.from(e.target.files || []); if (files.length > 0) handleAddEditImages(files); e.target.value = ''; }} />
                        </label>
                      </div>
                      <div className="flex space-x-2">
                        <button onClick={() => { setEditingExpId(null); setEditNewImages([]); }} className="text-xs bg-transparent text-foreground hover:bg-secondary rounded-full px-4 py-1 transition-colors" data-testid={`cancel-edit-story-${exp.id}`}>Cancel</button>
                        <button onClick={() => saveEditExp(exp.id)} className="text-xs bg-primary text-white hover:bg-primary/80 rounded-full px-4 py-1 transition-colors" data-testid={`save-edit-story-${exp.id}`}>Save</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 select-text" data-testid={`story-summary-${exp.id}`}>
                        {showAiSummary[exp.id] && exp.ai_summary ? (
                          <div>
                            <p className="text-base leading-relaxed text-foreground whitespace-pre-wrap select-text cursor-text">
                              <LinkifyText text={exp.ai_summary} />
                              <span className="text-accent italic font-medium text-sm ml-1">(ai summarised)</span>
                            </p>
                          </div>
                        ) : (
                          <p className="text-base leading-relaxed text-foreground whitespace-pre-wrap select-text cursor-text">
                            <LinkifyText text={(exp.summary || '').length > STORY_MAX_LENGTH && !expandedStories[exp.id]
                              ? (exp.summary || '').slice(0, STORY_MAX_LENGTH) + '...'
                              : (exp.summary || '')} />
                          </p>
                        )}
                        {!showAiSummary[exp.id] && (exp.summary || '').length > STORY_MAX_LENGTH && (
                          <button
                            onClick={() => setExpandedStories(prev => ({ ...prev, [exp.id]: !prev[exp.id] }))}
                            className="text-sm text-primary hover:text-primary/70 font-medium mt-1 transition-colors"
                            data-testid={`toggle-story-${exp.id}`}
                          >
                            {expandedStories[exp.id] ? 'Show less' : 'See more'}
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Action bar: date + author */}
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-xs text-muted-foreground tracking-wide uppercase">{new Date(exp.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                      {isSharedMode && exp.author_name && (
                        <span className="text-[10px] text-muted-foreground bg-secondary px-1.5 py-0.5 rounded-full">{exp.author_name}</span>
                      )}
                      {exp.shared_from && (
                        <span className="text-[10px] text-accent bg-accent/10 px-1.5 py-0.5 rounded-full font-medium">from {exp.shared_from}</span>
                      )}
                    </div>
                    {isMine && exp.needs_review && !exp.confirmed && (
                      <div className="mb-3 flex items-center justify-between bg-accent/10 border border-accent/20 rounded-lg px-3 py-2" data-testid={`review-tag-${exp.id}`}>
                        <span className="text-xs text-accent font-medium flex items-center space-x-1"><Sparkles className="w-3 h-3" /><span>AI sorted — review</span></span>
                        <div className="flex items-center space-x-2">
                          <button onClick={async () => { try { await axios.post(`${API}/experiences/${exp.id}/confirm`); await fetchData(); } catch (err) { console.error(err); } }} className="text-xs bg-primary text-white rounded-full px-2.5 py-1 font-medium hover:bg-primary/80 active:scale-95 flex items-center space-x-1" data-testid={`confirm-story-${exp.id}`}><Check className="w-3 h-3" /><span>Confirm</span></button>
                          <button onClick={() => setDeleteExpConfirm(exp.id)} className="text-xs text-red-400 hover:text-red-500 transition-colors" data-testid={`reject-story-${exp.id}`}>Remove</button>
                        </div>
                      </div>
                    )}
                    {isSharedWithMe && exp.shared_from && !exp.is_viewed && (
                      <div className="mb-3 flex items-center justify-between bg-accent/10 border border-accent/20 rounded-lg px-3 py-2" data-testid={`shared-review-tag-${exp.id}`}>
                        <span className="text-xs text-accent font-medium flex items-center space-x-1"><UserPlus className="w-3 h-3" /><span>New from {exp.shared_from}</span></span>
                        <div className="flex items-center space-x-2">
                          <button onClick={async () => { try { await axios.post(`${API}/experiences/mark-viewed`, { experience_ids: [exp.id] }); setExperiences(prev => prev.map(e => e.id === exp.id ? { ...e, is_viewed: true } : e)); } catch (err) { console.error(err); } }} className="text-xs bg-primary text-white rounded-full px-2.5 py-1 font-medium hover:bg-primary/80 active:scale-95 flex items-center space-x-1" data-testid={`keep-shared-story-${exp.id}`}><Check className="w-3 h-3" /><span>Keep</span></button>
                          <button onClick={() => setDeleteExpConfirm(exp.id)} className="text-xs text-red-400 hover:text-red-500 transition-colors" data-testid={`reject-shared-story-${exp.id}`}>Remove</button>
                        </div>
                      </div>
                    )}
                    {!isSharedMode && !isSharedWithMe && exp.source_entry_id && !exp.shared_from && (
                      <Link to={`/timeline?entry=${exp.source_entry_id}`} className="inline-flex items-center space-x-1 text-xs text-primary hover:text-[#7A9186] transition-colors" data-testid={`view-journal-link-${exp.id}`}><ArrowLeft className="w-3 h-3" /><span>View Journal Entry</span></Link>
                    )}
                    {exp.images && exp.images.length > 0 && (
                      <div className="flex flex-wrap gap-2 my-2" data-testid={`story-images-${exp.id}`}>
                        {exp.images.map((img, idx) => (
                          <button
                            key={idx}
                            onClick={() => { setViewerImages(exp.images); setViewerInitialIndex(idx); }}
                            className="focus:outline-none focus:ring-2 focus:ring-primary/50 rounded-lg"
                            data-testid={`story-image-${exp.id}-${idx}`}
                          >
                            <img
                              src={`${API}/journal/images/${img.id || img.filename}?variant=thumb`}
                              alt=""
                              className="w-16 h-16 rounded-lg object-cover border border-border hover:border-primary/50 transition-colors cursor-pointer"
                              loading="lazy"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                    {/* Perspectives - always visible below images */}
                    {showCommentsFeature && (
                      <div className="mt-3">
                        {(perspectives[exp.id] || []).length > 0 && (
                          <div className="space-y-2 mb-2">
                            {(perspectives[exp.id] || []).map((persp) => (
                              <div key={persp.id} className="flex gap-2 group/persp" data-testid={`perspective-${persp.id}`}>
                                <div className="w-6 h-6 rounded-full bg-accent/20 text-accent flex items-center justify-center text-[10px] font-medium overflow-hidden flex-shrink-0 mt-0.5">
                                  {persp.user_picture ? (
                                    <img src={persp.user_picture} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    persp.user_name?.charAt(0)?.toUpperCase() || '?'
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-xs font-medium text-foreground">{persp.user_name}</span>
                                    <span className="text-[10px] text-muted-foreground">{new Date(persp.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                    {persp.edited_at && <span className="text-[10px] text-muted-foreground italic">(edited)</span>}
                                  </div>
                                  {editingPerspectiveId === persp.id ? (
                                    <div className="mt-1">
                                      <textarea
                                        value={editPerspectiveContent}
                                        onChange={(e) => setEditPerspectiveContent(e.target.value)}
                                        className="w-full text-sm bg-transparent border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:border-primary resize-none min-h-[40px]"
                                        autoFocus
                                      />
                                      <div className="flex gap-1.5 mt-1">
                                        <button onClick={() => handleEditPerspective(exp.id, persp.id)} className="text-[10px] bg-primary text-white px-2 py-0.5 rounded-full">Save</button>
                                        <button onClick={() => setEditingPerspectiveId(null)} className="text-[10px] text-muted-foreground">Cancel</button>
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">{persp.content}</p>
                                  )}
                                  {persp.user_id === user?.user_id && editingPerspectiveId !== persp.id && (
                                    <div className="flex gap-2 mt-0.5 opacity-0 group-hover/persp:opacity-100 transition-opacity">
                                      <button onClick={() => { setEditingPerspectiveId(persp.id); setEditPerspectiveContent(persp.content); }} className="text-[10px] text-muted-foreground hover:text-primary">Edit</button>
                                      <button onClick={() => handleDeletePerspective(exp.id, persp.id)} className="text-[10px] text-muted-foreground hover:text-red-500">Delete</button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Add perspective input */}
                        {!(perspectives[exp.id] || []).some(p => p.user_id === user?.user_id) && (
                          <div className="flex gap-2 items-start">
                            <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-[10px] font-medium overflow-hidden flex-shrink-0 mt-0.5">
                              {user?.picture ? (
                                <img src={user.picture} alt="" className="w-full h-full object-cover" />
                              ) : (
                                user?.name?.charAt(0)?.toUpperCase() || '?'
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <textarea
                                value={perspectiveInput[exp.id] || ''}
                                onChange={(e) => setPerspectiveInput(prev => ({ ...prev, [exp.id]: e.target.value }))}
                                placeholder="Add your perspective..."
                                className="w-full text-sm bg-transparent border border-border/60 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-primary placeholder:text-muted-foreground/40 resize-none min-h-[36px]"
                                rows={1}
                              />
                              {(perspectiveInput[exp.id] || '').trim() && (
                                <button
                                  onClick={() => handleAddPerspective(exp.id)}
                                  disabled={submittingPerspective === exp.id}
                                  className="mt-1 text-xs bg-primary text-white px-3 py-1 rounded-full font-medium hover:bg-primary/80 transition-colors disabled:opacity-50"
                                >
                                  {submittingPerspective === exp.id ? 'Posting...' : 'Post'}
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="mt-2 flex items-center justify-between">
                      {/* Comment count - left */}
                      <div className="flex items-center gap-2">
                        {showCommentsFeature && (
                          <button
                            onClick={() => setOpenCommentsId(openCommentsId === exp.id ? null : exp.id)}
                            className={`flex items-center gap-1 text-xs transition-colors ${openCommentsId === exp.id ? 'text-primary' : 'text-muted-foreground hover:text-primary'}`}
                            data-testid={`comments-button-${exp.id}`}
                          >
                            <MessageCircle className="w-4 h-4" />
                            {commentCounts[exp.id] > 0 && <span>{commentCounts[exp.id]}</span>}
                          </button>
                        )}
                      </div>
                      {/* ⋯ menu - right */}
                      <div className="relative" ref={storyMenuOpen === exp.id ? storyMenuRef : null}>
                        <button
                          onClick={() => setStoryMenuOpen(storyMenuOpen === exp.id ? null : exp.id)}
                          className="text-muted-foreground hover:text-primary transition-colors p-1.5 rounded-full hover:bg-secondary/50"
                          data-testid={`story-menu-${exp.id}`}
                        >
                          <MoreHorizontal className="w-5 h-5" />
                        </button>
                        {storyMenuOpen === exp.id && (
                          <div className="absolute right-0 bottom-full mb-1 bg-card-solid border border-border rounded-xl shadow-lg py-1 min-w-[180px] z-50">
                            {/* Edit */}
                            {(isMine || (isSharedMode && isMine)) && !exp.shared_from && (
                              <button onClick={() => { startEditExp(exp); setStoryMenuOpen(null); }} className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-secondary/50 transition-colors flex items-center gap-3" data-testid={`edit-story-button-${exp.id}`}>
                                <Edit2 className="w-4 h-4 text-muted-foreground" /><span>Edit Story</span>
                              </button>
                            )}
                            {/* Add Images */}
                            {(isMine || (isSharedMode && isMine)) && !exp.shared_from && (
                              <label className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-secondary/50 transition-colors flex items-center gap-3 cursor-pointer" data-testid={`add-image-story-${exp.id}`}>
                                <ImagePlus className="w-4 h-4 text-muted-foreground" /><span>{uploadingImageForStory === exp.id ? 'Uploading...' : 'Add Images'}</span>
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleAddImageToStory(exp.id, file); e.target.value = ''; setStoryMenuOpen(null); }} />
                              </label>
                            )}
                            {/* Move to Folder */}
                            <button onClick={() => {
                              const storyFolders = new Set(experiences.filter(e => e.summary === exp.summary).map(e => e.topic_folder));
                              setInitialSelections(new Set(storyFolders));
                              setPendingSelections(new Set(storyFolders));
                              setPendingNewFolders([]);
                              setInlineFolderName('');
                              setAddToFolderOpen(exp.id);
                              setStoryMenuOpen(null);
                            }} className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-secondary/50 transition-colors flex items-center gap-3" data-testid={`add-to-folder-story-${exp.id}`}>
                              <FolderPlus className="w-4 h-4 text-muted-foreground" /><span>Move to Folder</span>
                            </button>
                            {/* Share Story */}
                            {!exp.shared_from && (
                              <button onClick={() => { setShareStoryId(exp.id); setStoryMenuOpen(null); }} className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-secondary/50 transition-colors flex items-center gap-3" data-testid={`share-story-button-${exp.id}`}>
                                <UserPlus className="w-4 h-4 text-muted-foreground" /><span>Share Story</span>
                              </button>
                            )}
                            {/* Delete */}
                            {(isMine || exp.shared_from) && (
                              <>
                                <div className="border-t border-border my-1" />
                                <button onClick={() => { setDeleteExpConfirm(exp.id); setStoryMenuOpen(null); }} className="w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-500/10 transition-colors flex items-center gap-3" data-testid={`delete-story-button-${exp.id}`}>
                                  <Trash2 className="w-4 h-4" /><span>{exp.shared_from ? 'Remove' : 'Delete'}</span>
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      {addToFolderOpen === exp.id && (
                        <div className="mt-2 pt-2 border-t border-border" data-testid={`folder-dropdown-${exp.id}`}>
                          <div className="mb-2">
                            <span className="text-xs font-medium text-muted-foreground">Select folders</span>
                          </div>
                          <div className="max-h-40 overflow-y-auto space-y-1">
                            <button
                              onClick={() => {
                                setPendingSelections(prev => {
                                  const next = new Set(prev);
                                  next.has('Unsorted Stories') ? next.delete('Unsorted Stories') : next.add('Unsorted Stories');
                                  return next;
                                });
                              }}
                              className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                                pendingSelections.has('Unsorted Stories') ? 'bg-primary/10' : 'hover:bg-secondary/50'
                              }`}
                              data-testid="assign-folder-option-unsorted"
                            >
                              <span className="flex items-center justify-between">
                                <span className={pendingSelections.has('Unsorted Stories') ? 'text-primary font-medium' : 'text-muted-foreground'}>Unsorted Stories</span>
                                {pendingSelections.has('Unsorted Stories') && (
                                  <span className="flex items-center space-x-1.5">
                                    <Check className="w-4 h-4 text-primary" />
                                    <XCircle className="w-4 h-4 text-red-400 hover:text-red-500" />
                                  </span>
                                )}
                              </span>
                            </button>
                            {folders.filter(f => !f.is_suggested).map(f => (
                              <button
                                key={f.id}
                                onClick={() => {
                                  setPendingSelections(prev => {
                                    const next = new Set(prev);
                                    next.has(f.name) ? next.delete(f.name) : next.add(f.name);
                                    return next;
                                  });
                                }}
                                className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                                  pendingSelections.has(f.name) ? 'bg-primary/10' : 'hover:bg-secondary/50'
                                }`}
                                data-testid={`assign-folder-option-${f.id}`}
                              >
                                <span className="flex items-center justify-between">
                                  <span className={pendingSelections.has(f.name) ? 'text-primary font-medium' : ''}>{f.name}</span>
                                  {pendingSelections.has(f.name) && (
                                    <span className="flex items-center space-x-1.5">
                                      <Check className="w-4 h-4 text-primary" />
                                      <XCircle className="w-4 h-4 text-red-400 hover:text-red-500" />
                                    </span>
                                  )}
                                </span>
                              </button>
                            ))}
                            {pendingNewFolders.map(name => (
                              <button
                                key={`new-${name}`}
                                onClick={() => {
                                  setPendingSelections(prev => {
                                    const next = new Set(prev);
                                    if (next.has(name)) {
                                      next.delete(name);
                                      setPendingNewFolders(p => p.filter(n => n !== name));
                                    } else {
                                      next.add(name);
                                    }
                                    return next;
                                  });
                                }}
                                className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                                  pendingSelections.has(name) ? 'bg-primary/10' : 'hover:bg-secondary/50'
                                }`}
                                data-testid={`assign-folder-option-new-${name}`}
                              >
                                <span className="flex items-center justify-between">
                                  <span className={pendingSelections.has(name) ? 'text-primary font-medium' : ''}>{name} <span className="text-xs italic text-muted-foreground">(new)</span></span>
                                  {pendingSelections.has(name) && (
                                    <span className="flex items-center space-x-1.5">
                                      <Check className="w-4 h-4 text-primary" />
                                      <XCircle className="w-4 h-4 text-red-400 hover:text-red-500" />
                                    </span>
                                  )}
                                </span>
                              </button>
                            ))}
                          </div>
                          <div className="mt-2 pt-2 border-t border-border">
                            <form
                              onSubmit={(e) => {
                                e.preventDefault();
                                const name = inlineFolderName.trim();
                                if (!name) return;
                                if (!pendingNewFolders.includes(name) && !folders.some(f => f.name === name)) {
                                  setPendingNewFolders(prev => [...prev, name]);
                                }
                                setPendingSelections(prev => new Set([...prev, name]));
                                setInlineFolderName('');
                              }}
                              className="flex items-center space-x-2"
                            >
                              <input
                                type="text"
                                value={inlineFolderName}
                                onChange={(e) => setInlineFolderName(e.target.value)}
                                placeholder="New folder..."
                                className="flex-1 text-sm bg-secondary/30 border-none rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary"
                                data-testid="inline-create-folder-input"
                              />
                              <button
                                type="submit"
                                disabled={!inlineFolderName.trim()}
                                className="bg-primary text-primary-foreground px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                                data-testid="inline-create-folder-submit"
                              >
                                <PlusCircle className="w-4 h-4" />
                              </button>
                            </form>
                          </div>
                          <div className="mt-3 bg-muted/60 rounded-xl px-3 py-2.5 flex items-center justify-end space-x-2">
                            <button
                              onClick={() => {
                                setAddToFolderOpen(null);
                                setPendingSelections(new Set());
                                setInitialSelections(new Set());
                                setPendingNewFolders([]);
                                setInlineFolderName('');
                              }}
                              className="px-4 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-lg"
                              data-testid={`folder-cancel-${exp.id}`}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={async () => {
                                setIsSavingFolders(true);
                                try {
                                  for (const name of pendingNewFolders) {
                                    try {
                                      await axios.post(`${API}/folders`, { name });
                                    } catch (err) { console.error(err); }
                                  }
                                  // For Unsorted folder: if user selected another folder, auto-remove from Unsorted
                                  let finalSelections = new Set(pendingSelections);
                                  if (topicId === 'unsorted') {
                                    const hasOtherFolder = [...finalSelections].some(f => f !== 'Unsorted Stories');
                                    if (hasOtherFolder && finalSelections.has('Unsorted Stories')) {
                                      finalSelections.delete('Unsorted Stories');
                                    }
                                  }
                                  const toAdd = [...finalSelections].filter(f => !initialSelections.has(f));
                                  const toRemove = [...initialSelections].filter(f => !finalSelections.has(f));
                                  for (const folderName of toAdd) {
                                    await handleAddToFolder(exp.id, folderName);
                                  }
                                  for (const folderName of toRemove) {
                                    const matchExp = experiences.find(e => e.summary === exp.summary && e.topic_folder === folderName);
                                    if (matchExp) await handleDeleteExp(matchExp.id);
                                  }
                                  await fetchData();
                                } catch (err) { console.error(err); }
                                setIsSavingFolders(false);
                                setAddToFolderOpen(null);
                                setPendingSelections(new Set());
                                setInitialSelections(new Set());
                                setPendingNewFolders([]);
                                setInlineFolderName('');
                              }}
                              disabled={isSavingFolders}
                              className="px-4 py-1.5 text-sm bg-foreground text-background font-medium rounded-lg hover:opacity-90 disabled:opacity-50 transition-colors"
                              data-testid={`folder-done-${exp.id}`}
                            >
                              {isSavingFolders ? 'Saving...' : 'Done'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Story Comments Section - show for collab folders */}
                    {showCommentsFeature && (
                      <StoryComments
                        storyId={exp.id}
                        isOpen={openCommentsId === exp.id}
                        onClose={() => setOpenCommentsId(null)}
                        currentUser={user}
                        onCountChange={(count) => setCommentCounts(prev => ({ ...prev, [exp.id]: count }))}
                      />
                    )}
                  </>
                )}
              </motion.div>
              </SwipeToDelete>
            );
            })}
          </div>
        )}

        {/* Delete Story Confirmation Modal */}
        {deleteExpConfirm && (() => {
          const deletingExp = experiences.find(e => e.id === deleteExpConfirm);
          const isLinkedCopy = isSharedWithMe || (deletingExp && deletingExp.shared_from);
          return (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="delete-story-confirmation-modal">
            <div className="bg-card-solid rounded-2xl p-6 max-w-md mx-4 shadow-2xl">
              <h3 className="text-xl font-serif font-medium mb-3">{isLinkedCopy ? 'Remove Story?' : 'Delete Story?'}</h3>
              <p className="text-muted-foreground mb-6">{isLinkedCopy ? 'Are you sure you want to remove your access to this story? You will no longer be able to view it.' : 'This action cannot be undone.'}</p>
              <div className="flex justify-end space-x-3">
                <button onClick={() => setDeleteExpConfirm(null)} className="bg-transparent text-foreground hover:bg-secondary rounded-full px-6 py-2 transition-colors" data-testid="cancel-delete-story-button">Cancel</button>
                <button onClick={() => handleDeleteExp(deleteExpConfirm)} className="bg-red-500 text-white hover:bg-red-600 rounded-full px-6 py-2 transition-colors" data-testid="confirm-delete-story-button">{isLinkedCopy ? 'Remove' : 'Delete'}</button>
              </div>
            </div>
          </motion.div>
          );
        })()}

        {/* Delete Folder Confirmation Modal */}
        {deleteFolderConfirm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="delete-folder-confirmation-modal">
            <div className="bg-card-solid rounded-2xl p-6 max-w-md mx-4 shadow-2xl">
              <h3 className="text-xl font-serif font-medium mb-3">Delete Folder?</h3>
              <p className="text-muted-foreground mb-6">This will delete the folder and all {topicExperiences.length} {topicExperiences.length === 1 ? 'story' : 'stories'} inside it. This cannot be undone.</p>
              <div className="flex justify-end space-x-3">
                <button onClick={() => setDeleteFolderConfirm(false)} className="bg-transparent text-foreground hover:bg-secondary rounded-full px-6 py-2 transition-colors" data-testid="cancel-delete-folder-button">Cancel</button>
                <button onClick={handleDeleteFolder} className="bg-red-500 text-white hover:bg-red-600 rounded-full px-6 py-2 transition-colors" data-testid="confirm-delete-folder-button">Delete Folder</button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Edit modals removed — replaced by inline edit-in-place in header */}
      </div>

      {/* Folder Settings Bottom Sheet */}
      <AnimatePresence>
        {showFolderMenu && folder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-end justify-center"
            onClick={() => { setShowFolderMenu(false); setActiveSection(null); }}
            data-testid="folder-settings-sheet"
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="bg-card-solid w-full max-w-lg rounded-t-3xl shadow-2xl overflow-hidden max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Top bar with drag handle and close button */}
              <div className="relative flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
                <button
                  onClick={() => { setShowFolderMenu(false); setActiveSection(null); }}
                  className="absolute right-4 top-3 text-muted-foreground hover:text-foreground transition-colors p-1 rounded-full hover:bg-secondary/50"
                  data-testid="close-sheet"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Folder header */}
              <div className="flex flex-col items-center px-6 pt-2 pb-4">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3" style={{ backgroundColor: folder?.color ? hexToRgba(folder.color, 0.12) : 'var(--color-primary-10, rgba(138, 163, 153, 0.1))' }}>
                  <FolderOpen className="w-8 h-8" style={{ color: folder?.color || 'var(--color-primary)' }} />
                </div>
                <h2 className="text-xl font-serif font-medium text-foreground text-center">{folder.name}</h2>
                {folder.description && (
                  <p className="text-sm text-primary/70 italic mt-1 text-center">"{folder.description}"</p>
                )}
                <p className="text-sm text-muted-foreground mt-1">{topicExperiences.length} {topicExperiences.length === 1 ? 'story' : 'stories'}</p>
              </div>

              {/* Action buttons row */}
              <div className="flex justify-center gap-6 px-6 pb-4">
                <button
                  onClick={() => setActiveSection(activeSection === 'share' ? null : 'share')}
                  className="flex flex-col items-center gap-1.5"
                  data-testid="sheet-share"
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${activeSection === 'share' ? 'bg-primary text-white' : 'bg-secondary hover:bg-secondary/70 text-foreground'}`}>
                    <UserPlus className="w-5 h-5" />
                  </div>
                  <span className="text-xs text-muted-foreground">Share</span>
                </button>
                <button
                  onClick={() => setActiveSection(activeSection === 'qr' ? null : 'qr')}
                  className="flex flex-col items-center gap-1.5"
                  data-testid="sheet-qr"
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${activeSection === 'qr' ? 'bg-primary text-white' : 'bg-secondary hover:bg-secondary/70 text-foreground'}`}>
                    <QrCode className="w-5 h-5" />
                  </div>
                  <span className="text-xs text-muted-foreground">QR</span>
                </button>
                {!isSharedMode && !isSharedWithMe && folder?.folder_type !== 'event' && (
                  <button
                    onClick={() => setActiveSection(activeSection === 'find' ? null : 'find')}
                    className="flex flex-col items-center gap-1.5"
                    data-testid="sheet-find"
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${activeSection === 'find' ? 'bg-primary text-white' : 'bg-secondary hover:bg-secondary/70 text-foreground'}`}>
                      <Search className="w-5 h-5" />
                    </div>
                    <span className="text-xs text-muted-foreground">Find</span>
                  </button>
                )}
                {!isSharedMode && !isSharedWithMe && (
                  <button
                    onClick={() => setActiveSection(activeSection === 'color' ? null : 'color')}
                    className="flex flex-col items-center gap-1.5"
                    data-testid="sheet-color"
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${activeSection === 'color' ? 'bg-primary text-white' : 'bg-secondary hover:bg-secondary/70 text-foreground'}`}>
                      <Palette className="w-5 h-5" />
                    </div>
                    <span className="text-xs text-muted-foreground">Color</span>
                  </button>
                )}
              </div>

              {/* Expandable sections */}
              <AnimatePresence mode="wait">
                {/* Share section */}
                {activeSection === 'share' && (
                  <motion.div
                    key="share"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden border-t border-border"
                  >
                    <div className="px-6 py-4 space-y-4">
                      {/* Email input - outlined, auto focused */}
                      <div className="relative">
                        <input
                          type="email"
                          value={inviteEmail}
                          onChange={(e) => searchUsers(e.target.value)}
                          placeholder="Add email..."
                          autoFocus
                          className="w-full bg-transparent border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 placeholder:text-muted-foreground/50"
                        />
                        {/* Search results from matching recent collabs + API */}
                        {inviteEmail.length >= 2 && !selectedInviteEmail && (() => {
                          const matchingRecent = recentCollabs.filter(e => e.toLowerCase().includes(inviteEmail.toLowerCase()));
                          const searchEmails = new Set(inviteSearchResults.map(u => u.email));
                          const combined = [
                            ...inviteSearchResults.map(u => ({ email: u.email, name: u.name, type: 'user' })),
                            ...matchingRecent.filter(e => !searchEmails.has(e)).map(e => ({ email: e, name: null, type: 'recent' }))
                          ];
                          return combined.length > 0 ? (
                            <div className="absolute left-0 right-0 top-full mt-1 bg-card-solid border border-border rounded-xl shadow-lg z-10 max-h-40 overflow-y-auto">
                              {combined.map((item) => (
                                <button key={item.email} onClick={() => { setSelectedInviteEmail(item.email); setInviteEmail(item.email); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-secondary/50 transition-colors flex items-center gap-2">
                                  <span className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs flex-shrink-0">{(item.name || item.email)?.charAt(0)?.toUpperCase() || '?'}</span>
                                  <span className="truncate">{item.email}</span>
                                </button>
                              ))}
                            </div>
                          ) : inviteEmail.includes('@') ? (
                            <div className="absolute left-0 right-0 top-full mt-1 bg-card-solid border border-border rounded-xl shadow-lg z-10">
                              <button onClick={() => { setSelectedInviteEmail(inviteEmail); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-secondary/50 transition-colors">
                                Select <span className="font-medium">{inviteEmail}</span>
                              </button>
                            </div>
                          ) : null;
                        })()}
                      </div>

                      {/* Selected email with Send button */}
                      {selectedInviteEmail && (
                        <div className="flex items-center gap-2 bg-secondary/30 rounded-xl px-4 py-2.5">
                          <span className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs flex-shrink-0">{selectedInviteEmail.charAt(0).toUpperCase()}</span>
                          <span className="text-sm text-foreground flex-1 truncate">{selectedInviteEmail}</span>
                          <button
                            onClick={() => { sendInvite(selectedInviteEmail); setSelectedInviteEmail(null); }}
                            className="bg-primary text-white rounded-lg px-3 py-1.5 text-xs font-medium hover:bg-primary/80 transition-colors flex items-center gap-1"
                          >
                            <Send className="w-3 h-3" />
                            Send
                          </button>
                          <button
                            onClick={() => { setSelectedInviteEmail(null); setInviteEmail(''); }}
                            className="text-muted-foreground hover:text-foreground p-1 transition-colors"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}

                      {inviteSuccess && <p className="text-xs text-primary font-medium">{inviteSuccess}</p>}

                      {/* Copy share link - green button */}
                      {shareLink ? (
                        <button onClick={copyShareLink} className={`w-full flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${linkCopied ? 'bg-primary/20 text-primary' : 'bg-primary text-white hover:bg-primary/80'}`}>
                          <Link2 className="w-4 h-4" />
                          <span>{linkCopied ? 'Link copied!' : 'Copy share link'}</span>
                        </button>
                      ) : (
                        <button onClick={createShareLink} disabled={shareLinkLoading} className="w-full flex items-center justify-center gap-2 bg-primary text-white rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-primary/80 transition-colors disabled:opacity-50">
                          <Link2 className="w-4 h-4" />
                          <span>{shareLinkLoading ? 'Creating link...' : 'Copy share link'}</span>
                        </button>
                      )}

                      {/* QR code inline */}
                      <div className="flex flex-col items-center pt-2 gap-2">
                        <div className="bg-white p-3 rounded-2xl shadow-sm border border-border">
                          <QRCodeSVG value={`${BASE_URL}/join/${folder.id}`} size={120} bgColor="#ffffff" fgColor="#1a1a1a" level="M" />
                        </div>
                        <p className="text-xs text-muted-foreground">Scan QR to join</p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* QR section */}
                {activeSection === 'qr' && (
                  <motion.div
                    key="qr"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden border-t border-border"
                  >
                    <div className="flex flex-col items-center px-6 py-6 gap-3">
                      <div className="bg-white p-4 rounded-2xl shadow-sm border border-border">
                        <QRCodeSVG value={`${BASE_URL}/join/${folder.id}`} size={160} bgColor="#ffffff" fgColor="#1a1a1a" level="M" />
                      </div>
                      <p className="text-xs text-muted-foreground">Scan QR to join</p>
                    </div>
                  </motion.div>
                )}

                {/* Find section */}
                {activeSection === 'find' && (
                  <motion.div
                    key="find"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden border-t border-border"
                  >
                    <div className="px-6 py-4 space-y-3">
                      <p className="text-xs text-muted-foreground">Find & extract relevant stories for this folder from your journal entries.</p>
                      {pendingStories.length === 0 && (
                        <button onClick={handleFindStories} disabled={findLoading} className="w-full bg-accent text-white rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-accent/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                          {findLoading ? <><Sparkles className="w-4 h-4 animate-spin" /><span>Searching...</span></> : <><Search className="w-4 h-4" /><span>Find Stories</span></>}
                        </button>
                      )}
                      {/* No stories found message */}
                      {findSearched && !findLoading && pendingStories.length === 0 && (
                        <div className="text-center py-3">
                          <p className="text-sm text-muted-foreground">No stories found</p>
                          <p className="text-xs text-muted-foreground/60 mt-1">No relevant stories could be extracted for this folder</p>
                        </div>
                      )}
                      {/* Pending stories for review */}
                      {pendingStories.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-foreground">{pendingStories.length} {pendingStories.length === 1 ? 'story' : 'stories'} found — confirm to publish:</p>
                          {pendingStories.map((story, idx) => (
                            <div key={idx} className="bg-secondary/30 rounded-xl p-3 flex items-start gap-3">
                              <p className="text-sm text-foreground flex-1 leading-relaxed">{story.summary}</p>
                              <div className="flex gap-1.5 flex-shrink-0">
                                <button onClick={() => confirmStory(story, idx)} className="w-8 h-8 rounded-full bg-primary/10 text-primary hover:bg-primary/20 flex items-center justify-center transition-colors" title="Confirm">
                                  <Check className="w-4 h-4" />
                                </button>
                                <button onClick={() => rejectStory(idx)} className="w-8 h-8 rounded-full bg-red-500/10 text-red-500 hover:bg-red-500/20 flex items-center justify-center transition-colors" title="Reject">
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Edit section */}
                {/* Color picker section */}
                {activeSection === 'color' && (
                  <motion.div
                    key="color"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden border-t border-border"
                  >
                    <div className="px-6 py-4">
                      <p className="text-xs text-muted-foreground mb-3">Choose a theme color for this folder</p>
                      <div className="flex flex-wrap gap-3 justify-center">
                        {FOLDER_COLORS.map((c) => (
                          <button
                            key={c.hex}
                            onClick={() => handleColorChange(c.hex)}
                            className="flex flex-col items-center gap-1 group"
                            title={c.name}
                          >
                            <div
                              className={`w-10 h-10 rounded-full transition-all ${folder?.color === c.hex ? 'ring-2 ring-offset-2 ring-foreground scale-110' : 'hover:scale-110'}`}
                              style={{ backgroundColor: c.hex }}
                            />
                            <span className="text-[10px] text-muted-foreground">{c.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Collaborators section */}
              <div className="border-t border-border">
                <button
                  onClick={() => setActiveSection(activeSection === 'collaborators' ? null : 'collaborators')}
                  className="w-full flex items-center px-6 py-4 hover:bg-secondary/30 transition-colors"
                  data-testid="sheet-collaborators"
                >
                  <Users className="w-5 h-5 text-muted-foreground mr-4 flex-shrink-0" />
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-foreground">Collaborators</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {collaboratorsList.length > 0 ? `${collaboratorsList.length} ${collaboratorsList.length === 1 ? 'person' : 'people'}` : 'Invite people to contribute'}
                    </p>
                  </div>
                  {collaboratorsList.length > 0 && (
                    <div className="flex items-center mr-2">
                      {collaboratorsList.slice(0, 3).map((collab, idx) => (
                        <div
                          key={collab.user_id || idx}
                          className="w-6 h-6 rounded-full border-2 border-zinc-200 dark:border-zinc-800 bg-primary/20 text-primary flex items-center justify-center text-[10px] font-medium overflow-hidden"
                          style={{ marginLeft: idx === 0 ? 0 : '-6px', zIndex: 3 - idx }}
                        >
                          {collab.picture ? (
                            <img src={collab.picture} alt="" className="w-full h-full object-cover" />
                          ) : (
                            collab.name?.charAt(0)?.toUpperCase() || '?'
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <ChevronRight className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${activeSection === 'collaborators' ? 'rotate-90' : ''}`} />
                </button>
                <AnimatePresence>
                  {activeSection === 'collaborators' && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-4">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Members</p>
                        {collaboratorsList.length === 0 ? (
                          <p className="text-sm text-muted-foreground/60 py-2">No collaborators yet. Share to invite people.</p>
                        ) : (
                          <div className="space-y-2">
                            {collaboratorsList.map((collab) => (
                              <div key={collab.user_id} className="flex items-center gap-3 bg-secondary/30 rounded-xl px-3 py-2.5">
                                <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center text-sm font-medium overflow-hidden flex-shrink-0">
                                  {collab.picture ? (
                                    <img src={collab.picture} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    collab.name?.charAt(0)?.toUpperCase() || '?'
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-foreground truncate">{collab.name}</p>
                                  {collab.email && <p className="text-xs text-muted-foreground truncate">{collab.email}</p>}
                                </div>
                                {isFolderOwner && (
                                  <button
                                    onClick={() => handleRemoveCollaborator(collab.share_id, collab.name)}
                                    className="text-muted-foreground/50 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-500/10 flex-shrink-0"
                                    title={`Remove ${collab.name}`}
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Permissions section — only visible to folder owner */}
              {!isSharedMode && !isSharedWithMe && effectiveId !== 'unsorted' && (
                <div className="border-t border-border">
                  <button
                    onClick={() => setActiveSection(activeSection === 'permissions' ? null : 'permissions')}
                    className="w-full flex items-center px-6 py-4 hover:bg-secondary/30 transition-colors"
                    data-testid="sheet-permissions"
                  >
                    <Shield className="w-5 h-5 text-muted-foreground mr-4 flex-shrink-0" />
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium text-foreground">Permissions</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {folder?.allow_member_sharing !== false ? 'Anyone can share' : 'Only you can share'}
                      </p>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${activeSection === 'permissions' ? 'rotate-90' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {activeSection === 'permissions' && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-4 space-y-3">
                          <div className="flex items-center justify-between bg-secondary/30 rounded-xl px-4 py-3">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-foreground">Members can invite others</p>
                              <p className="text-xs text-muted-foreground mt-0.5">Allow collaborators to share this folder with more people</p>
                            </div>
                            <button
                              onClick={handleToggleMemberSharing}
                              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ml-3 ${folder?.allow_member_sharing !== false ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                            >
                              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${folder?.allow_member_sharing !== false ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Delete section — owner only */}
              {!isSharedMode && !isSharedWithMe && (
                <div className="border-t border-border">
                  <button
                    onClick={() => { setShowFolderMenu(false); setActiveSection(null); setDeleteFolderConfirm(true); }}
                    className="w-full flex items-center px-6 py-4 hover:bg-red-500/10 transition-colors"
                    data-testid="sheet-delete"
                  >
                    <Trash2 className="w-5 h-5 text-red-500 mr-4 flex-shrink-0" />
                    <span className="text-sm font-medium text-red-500">Delete Folder</span>
                  </button>
                </div>
              )}

              {/* Bottom safe area */}
              <div className="h-8" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Folder Modal */}
      {showShareModal && folder && (
        <ShareModal type="folder" itemId={folder.id} itemName={folder.name} onClose={() => setShowShareModal(false)} />
      )}
      {/* Share Story Modal */}
      {shareStoryId && (
        <ShareModal type="story" itemId={shareStoryId} itemName="story" onClose={() => setShareStoryId(null)} />
      )}

      {/* Image Viewer Modal */}
      {viewerImages && (
        <ImageViewerModal
          images={viewerImages}
          initialIndex={viewerInitialIndex}
          onClose={() => setViewerImages(null)}
        />
      )}

      {/* Folder Tour - SpotlightTour style, one-time only */}
      {/* Tour is disabled - FOLDER_TOUR_STEPS is empty, so we guard against rendering */}
      {showFolderTour && FOLDER_TOUR_STEPS.length > 0 && (() => {
        const step = FOLDER_TOUR_STEPS[folderTourStep];
        if (!step) return null; // Guard clause to prevent crash
        const tooltipWidth = 320;
        const margin = 16;
        let tooltipStyle = { width: tooltipWidth };
        if (tourTargetRect) {
          let top = tourTargetRect.top + tourTargetRect.height + margin;
          let left = tourTargetRect.left + tourTargetRect.width / 2 - tooltipWidth / 2;
          left = Math.max(margin, Math.min(left, window.innerWidth - tooltipWidth - margin));
          if (top + 200 > window.innerHeight - margin) top = window.innerHeight - 220;
          tooltipStyle = { ...tooltipStyle, top, left };
        } else {
          tooltipStyle = { ...tooltipStyle, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
        }
        return (
          <div className="fixed inset-0 z-[100]" data-testid="folder-tour-overlay">
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              <defs>
                <mask id="folder-tour-mask">
                  <rect x="0" y="0" width="100%" height="100%" fill="white" />
                  {tourTargetRect && (
                    <rect x={tourTargetRect.left} y={tourTargetRect.top} width={tourTargetRect.width} height={tourTargetRect.height} rx="12" fill="black" />
                  )}
                </mask>
              </defs>
              <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.50)" mask="url(#folder-tour-mask)" className="pointer-events-auto" />
            </svg>
            {tourTargetRect && (
              <div className="absolute pointer-events-none border-[3px] border-primary rounded-2xl" style={{ top: tourTargetRect.top - 4, left: tourTargetRect.left - 4, width: tourTargetRect.width + 8, height: tourTargetRect.height + 8, boxShadow: '0 0 20px 8px hsl(var(--primary) / 0.4)' }} />
            )}
            <button onClick={completeFolderTour} className="absolute top-4 right-4 z-[120] text-white/70 hover:text-white flex items-center gap-1 text-sm bg-black/40 px-3 py-1.5 rounded-full" data-testid="folder-tour-skip">
              Skip <X className="w-4 h-4" />
            </button>
            <AnimatePresence mode="wait">
              <motion.div key={folderTourStep} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }} className="absolute z-[120] bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl border border-zinc-200 dark:border-zinc-700 p-5" style={tooltipStyle}>
                <div className="flex gap-1.5 mb-4 justify-center">
                  {FOLDER_TOUR_STEPS.map((_, i) => (
                    <div key={i} className={`h-1.5 rounded-full transition-all ${i === folderTourStep ? 'w-6 bg-primary' : i < folderTourStep ? 'w-1.5 bg-primary/50' : 'w-1.5 bg-zinc-300 dark:bg-zinc-600'}`} />
                  ))}
                </div>
                <h3 className="text-lg font-serif font-semibold text-zinc-900 dark:text-white mb-2 text-center">{step?.title}</h3>
                <p className="text-sm text-zinc-700 dark:text-zinc-200 text-center leading-relaxed mb-4">{step?.description}</p>
                <div className="flex items-center justify-between">
                  <button onClick={() => folderTourStep > 0 ? setFolderTourStep(folderTourStep - 1) : null} disabled={folderTourStep === 0} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${folderTourStep === 0 ? 'text-zinc-400 cursor-not-allowed' : 'text-zinc-600 hover:bg-zinc-100'}`} data-testid="folder-tour-back">
                    <ChevronLeft className="w-4 h-4" /> Back
                  </button>
                  <span className="text-xs text-zinc-500">{folderTourStep + 1} / {FOLDER_TOUR_STEPS.length}</span>
                  <button onClick={advanceFolderTour} className="flex items-center gap-1 px-4 py-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 text-sm font-medium" data-testid="folder-tour-next">
                    {folderTourStep === FOLDER_TOUR_STEPS.length - 1 ? 'Done' : 'Next'} {folderTourStep < FOLDER_TOUR_STEPS.length - 1 && <ChevronRight className="w-4 h-4" />}
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        );
      })()}
    </motion.div>
  );
}

export default TopicPage;
